import { t } from "@redotech/redo-i18n";
import type { ProductResumeState } from "@redotech/redo-onboarding-api/schema/get-resume-state";
import {
  getProductHighlightDescription,
  getProductStepLabel,
  getProductSubtextLabel,
} from "../product-labels";
import type { ProductEntry, ProductSetupHighlight } from "../products";

export type LandingProductSectionId = "self-serve" | "guided" | "active";

export interface LandingProductSection {
  id: LandingProductSectionId;
  products: ProductEntry[];
}

export type LandingProductDestination =
  | { type: "dashboard" }
  | { type: "dashboard-path"; path: string }
  | { type: "onboarding"; path: string };

export function getLandingProductSections(
  products: ProductEntry[],
  enabledProducts: Record<string, boolean> | null,
): LandingProductSection[] {
  const selfServe: ProductEntry[] = [];
  const guided: ProductEntry[] = [];
  const active: ProductEntry[] = [];

  for (const product of products) {
    if (enabledProducts?.[product.id]) {
      active.push(product);
    } else if (product.selfServe || product.externalRedirectPath) {
      selfServe.push(product);
    } else {
      guided.push(product);
    }
  }

  const sections: LandingProductSection[] = [
    { id: "self-serve", products: selfServe },
    { id: "guided", products: guided },
    { id: "active", products: active },
  ];

  return sections.filter((section) => section.products.length > 0);
}

export function getLandingProductSectionLabel(
  id: LandingProductSectionId,
): string {
  switch (id) {
    case "self-serve":
      return t("Self-serve setup");
    case "guided":
      return t("Assisted setup");
    case "active":
      return t("Active products");
  }
}

export function getDefaultLandingProductId(
  sections: LandingProductSection[],
  enabledProducts: Record<string, boolean> | null,
): string | undefined {
  const uiProducts = sections.flatMap((section) => section.products);
  if (uiProducts.length === 0) {
    return undefined;
  }

  if (!enabledProducts?.returns) {
    const returns = uiProducts.find((product) => product.id === "returns");
    if (returns) {
      return returns.id;
    }
  }

  return (
    uiProducts.find((product) => product.id !== "returns")?.id ??
    uiProducts[0]?.id
  );
}

export function getLandingProductHighlights(
  product: ProductEntry,
  catalogProduct: ProductEntry,
): ProductSetupHighlight[] {
  const requiresGuidedSetup =
    !product.selfServe && !product.externalRedirectPath;
  if (requiresGuidedSetup) {
    return [{ label: t("Meet with a Redo onboarding specialist") }];
  }

  if (catalogProduct.setupHighlights?.length) {
    return catalogProduct.setupHighlights
      .slice(0, 4)
      .map((highlight) => ({
        label: getProductStepLabel(highlight.label),
        ...(highlight.description
          ? {
              description: getProductHighlightDescription(
                highlight.description,
              ),
            }
          : {}),
      }));
  }

  const visibleSteps = catalogProduct.steps.filter((step) => !step.hideFromNav);
  const distinctLabels = Array.from(
    new Set(visibleSteps.map((step) => step.navGroup ?? step.label)),
  );

  if (
    product.externalRedirectPath &&
    distinctLabels.length === 1 &&
    distinctLabels[0] === "Schedule a call"
  ) {
    return [{ label: t("Continue setup in the Redo dashboard") }];
  }

  return distinctLabels
    .slice(0, 4)
    .map((label) => ({ label: getProductStepLabel(label) }));
}

export function getLandingProductSubtext(
  product: Pick<ProductEntry, "subtext">,
  options: {
    isActive: boolean;
    progress?: Pick<ProductResumeState, "completedSteps" | "totalSteps"> | null;
  },
): string {
  if (options.isActive) {
    return t("Open in the Redo dashboard");
  }
  if (options.progress && options.progress.totalSteps > 0) {
    return t("Steps completed: {{completedSteps}} of {{totalSteps}}", {
      completedSteps: options.progress.completedSteps,
      totalSteps: options.progress.totalSteps,
    });
  }
  return getProductSubtextLabel(product.subtext);
}

export function getLandingProductDestination(
  product: ProductEntry,
  isActive: boolean,
  search: string,
): LandingProductDestination {
  if (isActive) {
    return { type: "dashboard" };
  }
  if (product.externalRedirectPath) {
    return { type: "dashboard-path", path: product.externalRedirectPath };
  }
  return { type: "onboarding", path: `/${product.id}${search}` };
}
